import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, RefreshCw, Trash2, Activity, AlertCircle } from "lucide-react";

const FIELDS = [
  { key: "threshold_high",       label: "High anomaly threshold",    min: 0, max: 1, step: 0.01, hint: "Anomaly score ≥ this → HIGH." },
  { key: "threshold_medium",     label: "Medium anomaly threshold",  min: 0, max: 1, step: 0.01, hint: "Anomaly score ≥ this → MEDIUM." },
  { key: "reference_update_days",label: "Reference refresh (days)",  min: 1, max: 90, step: 1, hint: "Rebuild reference profiles after N days." },
  { key: "min_events_for_fft",   label: "Min events for FFT",        min: 2, max: 50, step: 1, hint: "Deals with fewer events are skipped." },
  { key: "bins",                 label: "FFT bins",                  min: 8, max: 512, step: 1, hint: "Frequency-bin resolution of the spectrum." },
];

export default function FFTSettings() {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/v1/fft/status");
    setCfg(data.config);
    setStatus(data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/v1/config/fft", cfg);
      setCfg(data.config);
      toast.success("FFT configuration saved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/v1/fft/reference/generate", {});
      toast.success(`Generated ${data.generated?.length ?? 0} reference profile(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const deleteProfile = async (deal_type) => {
    try {
      await api.delete(`/v1/fft/reference/${deal_type}`);
      toast.success(`Removed profile: ${deal_type}`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!cfg) return <div className="text-sm text-gray-400 py-8">Loading FFT config...</div>;

  return (
    <div className="space-y-6" data-testid="fft-settings">
      <div className="border border-gray-200 bg-white rounded-sm p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline text-gray-400 mb-1">Passive · Additive Signal</div>
            <div className="font-heading text-lg font-medium">FFT Anomaly Detector</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="overline text-gray-400">Enabled</span>
            <Switch
              checked={cfg.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
              data-testid="fft-enabled-switch"
            />
          </div>
        </div>

        <div className="space-y-5">
          {FIELDS.map((f) => (
            <div key={f.key} className="grid md:grid-cols-[1fr_auto] gap-4 items-start pb-5 border-b border-gray-100 last:border-b-0 last:pb-0">
              <div>
                <Label className="text-sm font-medium">{f.label}</Label>
                <div className="text-xs text-gray-500 mt-1">{f.hint}</div>
              </div>
              <Input
                type="number"
                min={f.min} max={f.max} step={f.step}
                value={cfg[f.key] ?? ""}
                onChange={(e) => setCfg({ ...cfg, [f.key]: e.target.value === "" ? null : Number(e.target.value) })}
                data-testid={`fft-cfg-${f.key}`}
                className="w-28 rounded-sm font-mono text-right"
              />
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <Button
            onClick={save} disabled={saving}
            data-testid="save-fft-config-btn"
            className="rounded-sm bg-black hover:bg-gray-800 gap-2"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Saving…" : "Save FFT config"}
          </Button>
        </div>
      </div>

      <div className="border border-gray-200 bg-white rounded-sm p-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-gray-400 mb-1">Reference Baselines</div>
            <div className="font-heading text-lg font-medium">Segment Profiles</div>
          </div>
          <Button
            onClick={generate} disabled={generating}
            variant="outline"
            data-testid="regenerate-profiles-btn"
            className="rounded-sm gap-2"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${generating ? "animate-spin" : ""}`} />
            {generating ? "Generating…" : "Regenerate all"}
          </Button>
        </div>
        <p className="text-sm text-gray-500 mb-4 leading-relaxed">
          Each profile is a normalized power spectrum averaged over LOW-risk deals of the segment. Every incoming deal is scored against its segment baseline.
        </p>

        {status?.profiles?.length === 0 ? (
          <div className="border border-dashed border-gray-300 rounded-sm p-8 text-center text-sm text-gray-500">
            <Activity className="w-5 h-5 mx-auto mb-2 text-gray-300" />
            No profiles yet. Run an analysis or click &ldquo;Regenerate all&rdquo;.
          </div>
        ) : (
          <div className="space-y-2" data-testid="profiles-list">
            {status?.profiles?.map((p) => (
              <div
                key={p.id}
                className={`border rounded-sm p-4 flex items-center justify-between ${
                  p.expired ? "border-red-200 bg-red-50/50" : "border-gray-200"
                }`}
                data-testid={`profile-${p.deal_type}`}
              >
                <div className="flex items-center gap-4">
                  <span className={`risk-pill ${p.expired ? "high" : "low"}`}>
                    {p.deal_type}
                  </span>
                  <div className="text-xs text-gray-500 font-mono">
                    n={p.sample_size} · bins={p.bins} · sr={p.sampling_rate_hz?.toExponential(2)} Hz
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className={`text-xs ${p.expired ? "text-red-600" : "text-gray-500"}`}>
                    {p.expired ? (
                      <span className="flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> expired
                      </span>
                    ) : (
                      <span>expires in {p.days_until_expiry}d</span>
                    )}
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => deleteProfile(p.deal_type)}
                    data-testid={`delete-profile-${p.deal_type}`}
                    className="rounded-sm h-7 px-2 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 pt-5 border-t border-gray-100 text-xs font-mono text-gray-500 flex justify-between">
          <span>total_analyses</span>
          <span>{status?.total_analyses ?? 0}</span>
        </div>
      </div>
    </div>
  );
}
